import React, { useEffect, useState } from 'react';
import ReactModal from 'react-modal';
import { useFormik } from 'formik';
import * as yup from 'yup';
import {
  CommonDropdown,
  CommonInput,
  RegularButton,
  FormErrorWrap,
  ICommonDropdownOption,
  AlertAdapter,
} from 'pg-ui-library';
import closeSvg from 'src/assets/close.svg';
import MileageService from 'src/services/mileage.service';
import { CloseIcon } from 'src/modules/login/components/mfa-modal/styles';
import {
  MileageConfigurationBody,
  MileageConfigurationFooter,
  MileageConfigurationForm,
  MileageConfigurationHeader,
  FormGrid,
  FieldWrapper,
} from './styles';

const countryCurrencyMap: Record<string, string> = {
  India: 'Indian Rupee',
  'United States': 'USD',
  Canada: 'CAD',
};

export const MileageCreateModal: React.FC<{
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  refetchMileage: () => void;
}> = ({ isOpen, setIsOpen, refetchMileage }) => {
  const handleModal = () => setIsOpen(!isOpen);

  const currentYear = new Date().getFullYear();

  const yearOptions: ICommonDropdownOption[] = Array.from({ length: 5 }).map((_, i) => {
    const year = currentYear - i;
    return { key: `${year}`, label: `${year}`, value: `${year}` };
  });

  const countryOptions: ICommonDropdownOption[] = [
    { key: 'India', label: 'India', value: 'India' },
    { key: 'United States', label: 'United States', value: 'United States' },
    { key: 'Canada', label: 'Canada', value: 'Canada' },
  ];

  const [selectedCountry, setSelectedCountry] = useState<string>('India');
  const [selectedYear, setSelectedYear] = useState<string>(`${currentYear}`);

  const validationSchema = yup.object({
    country: yup.string().required('Country is required'),
    year: yup.string().required('Year is required'),
    ratePerMile: yup.number().required('Rate is required').positive(),
  });

  const formik = useFormik({
    initialValues: {
      country: selectedCountry,
      currency: countryCurrencyMap[selectedCountry],
      year: selectedYear,
      ratePerMile: '',
      active: true,
    },
    validationSchema,
    onSubmit: async values => {
      try {
        await MileageService.createMileage({
          ...values,
          year: Number(values.year),
          ratePerMile: Number(values.ratePerMile),
        });

        AlertAdapter('Mileage created successfully!', 'success');
        refetchMileage();
        handleModal();
      } catch (err: any) {
        AlertAdapter(err.message || 'Failed to create mileage', 'error');
      }
    },
  });

  // 🔹 Auto update currency when country changes
  useEffect(() => {
    const currency = countryCurrencyMap[selectedCountry];
    formik.setFieldValue('country', selectedCountry);
    formik.setFieldValue('currency', currency);
  }, [selectedCountry]);

  useEffect(() => {
    formik.setFieldValue('year', selectedYear);
  }, [selectedYear]);

  return (
    <ReactModal
      isOpen={isOpen}
      ariaHideApp={false}
      onRequestClose={handleModal}
      style={{
        overlay: { backgroundColor: 'rgba(0,0,0,0.6)' },
        content: {
          width: '520px',
          margin: 'auto',
          borderRadius: '20px',
          padding: '20px',
          background: '#F3F4F8',
          height: '500px',
          flexDirection: 'column',
          justifyContent: 'center',
        },
      }}
    >
      <MileageConfigurationForm onSubmit={formik.handleSubmit}>
        <MileageConfigurationHeader>
          <h3>Create Mileage</h3>
          <CloseIcon src={closeSvg} onClick={handleModal} />
        </MileageConfigurationHeader>

        <MileageConfigurationBody>
          <FormGrid>
            {/* Country */}
            <FieldWrapper>
              <CommonDropdown label="Country" options={countryOptions} setValue={setSelectedCountry} />
              <FormErrorWrap visible errorMessage={formik.errors.country || ''} />
            </FieldWrapper>

            <FieldWrapper>
              <CommonInput label="Currency" value={formik.values.currency} disabled />
            </FieldWrapper>

            {/* Currency (full width) */}
            <FieldWrapper>
              <CommonDropdown label="Year" options={yearOptions} setValue={setSelectedYear} />
              <FormErrorWrap visible errorMessage={formik.errors.year || ''} />
            </FieldWrapper>

            {/* Rate (full width) */}
            <FieldWrapper>
              <CommonInput
                label="Rate Per Mile"
                placeholder="Enter rate"
                onChange={e => formik.setFieldValue('ratePerMile', e.target.value)}
              />
              <FormErrorWrap visible errorMessage={formik.errors.ratePerMile || ''} />
            </FieldWrapper>
          </FormGrid>

          <MileageConfigurationFooter>
            <RegularButton type="submit" disabled={!formik.isValid || formik.isSubmitting}>
              Create
            </RegularButton>
          </MileageConfigurationFooter>
        </MileageConfigurationBody>
      </MileageConfigurationForm>
    </ReactModal>
  );
};
